'use client';

import { motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { VipIcon } from '@/app/components/icons/VipIcon';
import { FormSubmitButton } from '@/app/components/shared/FormSubmitButton';
import type { TaskInfo } from '@/app/types';
import { cn } from '@/app/utils';
import type { getSubmitTaskType } from './utils';

interface HomeGenerationFormSubmitButtonProps {
  isH5: boolean;
  isSubmitDisabled: boolean;
  isProcessing: boolean;
  maxImageCount: number;
  onBeforeSubmit: () => boolean | Promise<boolean>;
  onCheckSettings: () => void;
  onSubmitSuccess: (task: TaskInfo) => 'suppressDefaultToast';
  shouldUpgradeForModel: boolean;
  submitTaskType: ReturnType<typeof getSubmitTaskType>;
  validationErrorMessage?: string;
}

export const HomeGenerationFormSubmitButton = ({
  isH5,
  isProcessing,
  isSubmitDisabled,
  maxImageCount,
  onBeforeSubmit,
  onCheckSettings,
  onSubmitSuccess,
  shouldUpgradeForModel,
  submitTaskType,
  validationErrorMessage,
}: HomeGenerationFormSubmitButtonProps) => {
  const shouldUseCompactDisabledState = isSubmitDisabled && !isProcessing;

  return (
    <FormSubmitButton
      // 只过渡颜色/背景等确定属性；宽度形变靠内容淡入缩放柔化。
      // 不用 framer-motion 的 layout：它会对输入时 textarea 增高引发的每次回流都补间，导致打字抖动。
      className={cn(
        'shrink-0 rounded-full transition-colors duration-300',
        shouldUseCompactDisabledState
          ? cn(
              'bg-white/[0.08] px-0 text-system-white48 hover:bg-white/[0.08] hover:text-system-white48',
              isH5 ? 'size-8' : 'size-9',
            )
          : 'h-9 min-h-0 w-auto gap-1 bg-white px-3 text-sm text-system-black shadow-[0_8px_24px_rgba(0,0,0,0.22)] hover:bg-white',
      )}
      maxImageCount={maxImageCount}
      taskType={submitTaskType}
      processing={isProcessing}
      disabled={isSubmitDisabled}
      disabledTip={validationErrorMessage}
      displayCredits={!shouldUseCompactDisabledState}
      onTrialsUpdated={onCheckSettings}
      onBeforeSubmit={onBeforeSubmit}
      extra={shouldUpgradeForModel ? <VipIcon className="size-4" /> : undefined}
      onSubmitSuccess={onSubmitSuccess}
      creditsPlacement={shouldUseCompactDisabledState ? undefined : 'beforeChildren'}
    >
      <motion.div
        key={shouldUseCompactDisabledState ? 'disabled' : 'enabled'}
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className={cn('flex items-center gap-2.5', !shouldUseCompactDisabledState && 'ml-1')}
      >
        {!shouldUseCompactDisabledState && <div className="h-4 w-px bg-black/[0.16]" aria-hidden="true" />}
        <ArrowUp className="size-5" />
      </motion.div>
    </FormSubmitButton>
  );
};
